import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { Badge, Radio, Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { BILLING_CYCLE } from '@hotjar/checkout/src/types';

const opacityAnimation = keyframes`
 0%, 100% { opacity: 1 }
 50% { opacity: .4 }
`;

const S = {
  Container: styled.div<{ checked: boolean }>`
    display: flex;
    flex: 1;
    border-radius: ${tokens.radius.m};
    border: 1px solid ${tokens.color.ui_border};

    &:hover {
      border: 1px solid ${tokens.color.ui_border_alt};
    }

    ${({ checked }) =>
      checked &&
      css`
        background-color: ${tokens.color.bg_active};
        border-color: ${tokens.color.ui_active};

        &:hover {
          background-color: ${tokens.color.bg_active};
          border-color: ${tokens.color.ui_active};
        }
      `}
  `,
  Badge: styled(Badge)<{ checked: boolean }>`
    color: ${tokens.color.text_success};
    background-color: ${tokens.color.bg_success};
    margin-right: ${tokens.spacer.x0};

    ${({ checked }) =>
      checked &&
      css`
        color: ${tokens.color.text_default};
        background-color: ${tokens.color.white};
      `}
  `,
  Radio: styled(Radio)`
    flex: 1;
    margin: 0;
    padding: ${tokens.spacer.x2} ${tokens.spacer.x3};

    /* 
      this is a workaround to override insights.css 
      TODO: remove when we Radio component does this for us
    */
    text-transform: unset;

    /* access the actual labelContent span */
    span:last-of-type {
      flex: 1;
    }
  `,
  Label: styled.div`
    ${tokens.flexContainer('form', { gap: tokens.spacer.x1 })};

    > * {
      margin: 0;
    }
  `,
  Loader: styled.div`
    display: flex;
    flex-wrap: wrap;
    animation: ${opacityAnimation} 4s infinite;

    &:before,
    &:after {
      content: '';
      display: inline-block;
      width: 100%;
    }

    &:before {
      background: ${tokens.color.neutral_300};
      height: ${tokens.spacer.x2};
    }

    &:after {
      background: ${tokens.color.neutral_200};
      height: ${tokens.spacer.x1};
      margin: ${tokens.spacer.x1} 0;
    }
  `,
};

interface BillingCycleOptionProps {
  id: string;
  name: string;
  amounts?: IBillingAmounts;
  onClick: (billingCycle: BILLING_CYCLE) => void;
  billingCycle: BILLING_CYCLE;
  checked: boolean;
}

export const BillingCycleOption = ({
  id,
  name,
  amounts,
  onClick,
  billingCycle,
  checked,
}: BillingCycleOptionProps): JSX.Element => {
  const { t } = useTranslationWithPrefix(`billing:payment-details.billing-cycle.${billingCycle}`);

  const isYearly = billingCycle === BILLING_CYCLE.yearly;
  const handleClick = () => onClick(billingCycle);

  return (
    <S.Container id={id} checked={checked}>
      <S.Radio
        id={id}
        name={name}
        value={billingCycle}
        isChecked={checked}
        onChange={handleClick}
        labelContent={
          <S.Label>
            <Text as="p" textType="subtitle2">
              {t('title')}
            </Text>
            {amounts ? (
              <>
                <Text as="p" textType="subtitle2">
                  {t('cost-per-month', { amount: amounts.discountedMonthlyAmount })}
                </Text>
                {isYearly && (
                  <Text as="p" textType="caption">
                    {t(
                      `description.${
                        amounts?.futureSavingsPercentage ? 'future-savings' : 'non-future-savings'
                      }`,
                      { amount: amounts?.payableAmount },
                    )}
                  </Text>
                )}
              </>
            ) : (
              <S.Loader />
            )}
            {amounts && amounts.savingsPercentage > 0 && (
              <div>
                <S.Badge kind="custom" checked={checked}>
                  {t('save.percentage', { percentage: amounts.savingsPercentage })}
                </S.Badge>
                <Text
                  as="span"
                  textType="subtitle2"
                  textColor={checked ? 'text_default' : 'text_success'}
                >
                  {t('save.amount', { amount: amounts.savingsAmount })}
                </Text>
              </div>
            )}
          </S.Label>
        }
      />
    </S.Container>
  );
};
